import {getValueFromLabelValue, isLabelValueEmpty} from '../utils/checkObjects';
import {Config} from 'olympe';
import {isAddressEmpty} from '../utils/address';
import {isValidDate, isValidFutureDate} from '../utils/date';
import {AccountDetailsByMaterial} from '../models/financial_infos/AccountDetailsByMaterial';
import {sendEmailLambda} from '../utils/functionSignatures';
import {getMailContentPRRejected} from '../utils/email';
import {PurchaseRequest} from '../models/PurchaseRequest';
import {defaultCollectiveMedicalVendorTopicOptions} from '../components/_helpers';
import {OneTimeVendor} from '../models/OneTimeVendor';
import {CurrencyController} from './CurrencyController';

/**
 * Static class that control purchase requests.
 */
export class SubmitController {
    /**
     *
     * @param {any} value
     * @param {string} property
     * @param {string} section
     * @param {PRValidation} validation
     * @param {string} customMsg
     */
    static isValueMissing(value, property, section, validation, customMsg) {
        if (!value) {
            const msg = customMsg || `You have to choose a ${property} under ${section}.`;
            validation.errorMessages.push(msg);
        }
    }

    /**
     *
     * @param {LabelValue} data
     * @param {string} property
     * @param {string} section
     * @param {PRValidation} validation
     */
    static isLableValueSame(data, property, section, validation) {
        if (data && data.value !== '' && data.value === data.label) {
            const msg = `You have to choose a ${property} under ${section}.`;
            validation.errorMessages.push(msg);
        }
    }

    /**
     *
     * @param {any} value
     * @param {string} wayOfBuying
     * @param {PRValidation} validation
     * @param {string} section
     * @return {Boolean} true/false
     */
    static isNotPastDate(value, wayOfBuying, validation, section) {
        if (value && !isValidFutureDate(value)) {
            let msg = wayOfBuying ==='FO' ? 'Check Validity end date as it cannot be in the past.' : 'The delivery date needs to be changed as it cannot be in the past.';
            if (section) {
                msg = `${section}: ${msg}`;
            }
            validation.errorMessages.push(msg);
            return false;
        }
        return true;
    }
    /**
     *
     * @param {Array} materials
     * @param {PRValidation} validation
     */
    static materialDateIsNotPastDate(materials, validation) {
        if (materials) {
            let stopChecking = false;
            materials.forEach((material) =>{
                if ( !stopChecking) {
                    stopChecking = !this.isNotPastDate(material.requestedDeliveryDate, 'Not Applicable', validation, 'Item Details');
                }
            });
        }
    }

    /**
     * Checks if only one type of Currency it's used in the material
     * @param {Array} materials
     * @param {PRValidation} validation
     */
    static materialsCurrencyCheck(materials, validation) {
        if (materials && materials.length> 1) {
            const allEqual = materials.every((material) => material.currency.value === materials[0].currency.value);
            if (!allEqual) {
                validation.errorMessages.push('Different currencies are not allowed on the same PR. Please correct the currencies and submit the PR.');
            }
        }
    }

    /**
     * @param {AccountDetailsByMaterial[]} accountDetailsByMaterial
     * @param {PRValidation} validation
     */
    static isSomethingMissingInAccounts(accountDetailsByMaterial, validation) {
        const missingValue = {
            at: false,
            cc: false,
            wbs: false,
            pc: false,
            gl: false,
        };
        for (const adbm of accountDetailsByMaterial) {
            const accountType = adbm.accounts[0].accountType; // All the accounts must have the same account type, so we can take the first one.
            if (!accountType.value && accountType.label !== 'Blank (no account assignment needed)' && !missingValue.at) {
                validation.errorMessages.push(`You have to choose an Account type under Account details.`);
                missingValue.at = true;
            } else {
                if (accountType.value === 'K' && // If account type is cost center and there is no value
                    !missingValue.cc &&
                    adbm.accounts.map((a) => a.costCenter).some((c) => isLabelValueEmpty(c))
                ) {
                    validation.errorMessages.push('You have at least one account without Cost center.');
                    missingValue.cc = true;
                } else if (accountType.value === 'P' && // If account type is WBS/Project and there is no value
                    !missingValue.wbs &&
                    adbm.accounts.map((a) => a.wbs).some((w) => isLabelValueEmpty(w))
                ) {
                    validation.errorMessages.push('You have at least one account without WBS/Project.');
                    missingValue.wbs = true;
                } else if (accountType.value === '2' && // If account type is Profit Center and there is no value
                    !missingValue.pc &&
                    adbm.accounts.map((a) => a.profitCenter).some((p) => isLabelValueEmpty(p))
                ) { // If account type is Profit center
                    validation.errorMessages.push('You have at least one account without Profit center.');
                    missingValue.pc = true;
                } else if (accountType.value === 'S') { // If account type is Statistical WBS
                    if (adbm.accounts.map((a) => a.costCenter).some((c) => isLabelValueEmpty(c)) && // and there is no cost center
                        !missingValue.cc
                    ) {
                        validation.errorMessages.push('You have at least one account without Cost center.');
                        missingValue.cc = true;
                    }
                    if (adbm.accounts.map((a) => a.wbs).some((w) => isLabelValueEmpty(w)) && // and there is no wbs
                        !missingValue.wbs
                    ) {
                        validation.errorMessages.push('You have at least one account without WBS/Project.');
                        missingValue.wbs = true;
                    }
                }
                if (!(accountType.value === '') && !(accountType.value === 'U') && !missingValue.gl) {
                    adbm.accounts.map((a) => a.glNumber).some((g) => isLabelValueEmpty(g)) ? validation.errorMessages.push('You have at least one account without G/L number.') : null;
                    missingValue.gl = true;
                }
            }
            if (Object.values(missingValue).every((v) => v === true)) { // If we found an error in each field, we can stop the search
                break;
            }
        }
    }

    /**
     * @param {string} vendorCode
     * @param {LabelValue} wayOfBuying
     * @param {LabelValue} spender
     * @param {LabelValue} requester
     * @param {LabelValue} companyCode
     * @param {LabelValue} purchaseOrganization
     * @param {LabelValue} purchasingGroup
     * @param {LabelValue} plant
     * @param {DeliveryAddress} deliveryAddress
     * @param {Date} requestedDeliveryDate
     * @param {Date} validityStart
     * @param {Date} validityEnd
     * @param {Material[]} materials
     * @param {Account[]} accounts
     * @param {AccountDetailsByMaterial[]} accountDetailsByMaterial
     * @param {boolean} areAccountSplitByMaterial
     * @param {PRValidation} validation
     * @param {string} OTVName
     * @param {DeliveryAddress} OTVAddress
     * @param {string} supplierType
     * @param {boolean} hasException
     * @param {string} selectedException
     * @param {Attachment[]} mainAttachments
     * @param {Attachment[]} cbAttachments
     * @param {LabelValue} newPaymentTerms
     * @param {string} userIdToCallSAPAPIs
     * @param {object} context
     * @param {boolean} mtvPowerBIValidated
     */
    static async checkMandatoryFields(
        vendorCode,
        wayOfBuying,
        spender,
        requester,
        companyCode,
        purchaseOrganization,
        purchasingGroup,
        plant,
        deliveryAddress,
        requestedDeliveryDate,
        validityStart,
        validityEnd,
        materials,
        accounts,
        accountDetailsByMaterial,
        areAccountSplitByMaterial,
        validation,
        OTVName,
        OTVAddress,
        supplierType,
        hasException,
        selectedException,
        mainAttachments,
        cbAttachments,
        newPaymentTerms,
        userIdToCallSAPAPIs,
        context,
        mtvPowerBIValidated,
    ) {
        this.checkMandatoryFieldsForSpender(
            vendorCode,
            wayOfBuying,
            spender,
            companyCode,
            purchaseOrganization,
            plant,
            deliveryAddress,
            requestedDeliveryDate,
            validityStart,
            validityEnd,
            materials,
            accounts,
            accountDetailsByMaterial,
            areAccountSplitByMaterial,
            validation,
            OTVName,
            OTVAddress,
            supplierType,
            hasException,
            selectedException,
            mainAttachments,
            cbAttachments,
            newPaymentTerms,
            mtvPowerBIValidated,
        );
        this.isValueMissing(wayOfBuying.value, 'Way of buying', 'General details', validation);
        this.isValueMissing(requester.value, 'Requester', 'General details', validation);
        this.isValueMissing(purchasingGroup.value, 'Purchasing group', 'General details', validation);

        if (Config.getParameter('ENV') !== 'codev') {
            materials.map((m) => m.taxCode).some((c) => isLabelValueEmpty(c)) ? validation.errorMessages.push('You have at least one material without tax code.') : null;
        }

        if (companyCode.value.startsWith('CH')) {
            if (Array.from(String(vendorCode)).every((char) => char === '9') || String(vendorCode) === '0') {
                validation.errorMessages.push('You have to choose a Supplier/Vendor code for your purchase requisition.');
            }
        }

        validation.error = validation.errorMessages.length > 0;
    }

    /**
     * @param {string} vendorCode
     * @param {LabelValue} wayOfBuying
     * @param {LabelValue} spender
     * @param {LabelValue} companyCode
     * @param {LabelValue} purchaseOrganization
     * @param {LabelValue} plant
     * @param {DeliveryAddress} deliveryAddress
     * @param {Date} requestedDeliveryDate
     * @param {Date} validityStart
     * @param {Date} validityEnd
     * @param {Material[]} materials
     * @param {Account[]} accounts
     * @param {AccountDetailsByMaterial[]} accountDetailsByMaterial
     * @param {boolean} areAccountSplitByMaterial
     * @param {PRValidation} validation
     * @param {string} OTVName
     * @param {DeliveryAddress} OTVAddress
     * @param {string} supplierType
     * @param {boolean} hasException
     * @param {string} selectedException
     * @param {Attachment[]} mainAttachments
     * @param {Attachment[]} cbAttachments
     * @param {LabelValue} newPaymentTerms
     * @param {boolean} mtvPowerBIValidated
     */
    static checkMandatoryFieldsForSpender(
        vendorCode,
        wayOfBuying,
        spender,
        companyCode,
        purchaseOrganization,
        plant,
        deliveryAddress,
        requestedDeliveryDate,
        validityStart,
        validityEnd,
        materials,
        accounts,
        accountDetailsByMaterial,
        areAccountSplitByMaterial,
        validation,
        OTVName,
        OTVAddress,
        supplierType,
        hasException,
        selectedException,
        mainAttachments,
        cbAttachments,
        newPaymentTerms,
        mtvPowerBIValidated,
    ) {
        if ((Array.from(String(vendorCode)).every((char) => char === '9') || String(vendorCode) === '0') &&
            !companyCode.value.startsWith('CH')) {
            validation.errorMessages.push('You have to choose a Supplier/Vendor code for your purchase requisition.');
        }
        this.isValueMissing(spender.value, 'Spender', 'General details', validation);
        this.isValueMissing(companyCode.value, 'Company code', 'General details', validation);
        this.isValueMissing(purchaseOrganization.value, 'Purchase organization', 'General details', validation);
        this.isValueMissing(plant.value, 'Plant', 'General details', validation);
        this.isValueMissing(!isAddressEmpty(deliveryAddress), 'Delivery address', 'General details', validation);

        const isAnOTV = supplierType === 'OTV';
        const isACMV = supplierType === 'CMV';
        const isAnMTV = supplierType === 'MTV';

        if (isAnOTV || isACMV || isAnMTV) {
            this.isValueMissing(
                !isAddressEmpty(OTVAddress),
                `${isAnOTV ? 'OTV' : isAnMTV ? 'MTV' : 'CMV'}'s address`,
                'Define supplier popup',
                validation,
            );

            if (
                isAnOTV &&
                (!mainAttachments?.find((a) =>
                    defaultCollectiveMedicalVendorTopicOptions
                        .slice(0, 1)
                        .some((topic) => a.topic.includes(topic)),
                ) || mainAttachments.length === 0)
            ) {
                validation.errorMessages.push(
                    `Missing documents for OTV: please open the "Supplier search engine" <POP-UP> or use the above "Optional document" section to attach the following document as required by your market policy:\nBanking Details`,
                );
            }

            if (
                isACMV &&
                (!mainAttachments?.find((a) =>
                    defaultCollectiveMedicalVendorTopicOptions.some((topic) =>
                        a.topic.includes(topic),
                    ),
                ) || mainAttachments.length === 0)
            ) {
                validation.errorMessages.push(
                    `Missing documents for CMV: please open the "Supplier search engine" <POP-UP> or use the above "Optional document" section to attach the documents your market policy may require between:\nBanking Details, Sponsorship request letter, Speaker acceptance.`,
                );
            }

            if (
                isAnMTV &&
                (!mainAttachments?.find((a) =>
                    defaultCollectiveMedicalVendorTopicOptions
                        .slice(0, 1)
                        .some((topic) => a.topic.includes(topic)),
                ) || mainAttachments.length === 0)
            ) {
                validation.errorMessages.push(
                    `Missing documents for MTV: please open the "Supplier search engine" <POP-UP> or use the above "Optional document" section to attach the following document as required by your market policy:\nBanking Details`,
                );
            }

            if (isAnMTV && !mtvPowerBIValidated) {
                validation.errorMessages.push(
                    'Please validate the MTV Annual Spend Report before submitting the Purchase Request.',
                );
            }

            if (isAnMTV) {
                const allowedMTVMaterialTypes = ['ZSIM', 'ZTRV', 'ZTRN'];
                const invalidMaterials = materials.filter(
                    (m) => m.type && !allowedMTVMaterialTypes.includes(m.type),
                );

                if (invalidMaterials.length > 0) {
                    const invalidTypes = [...new Set(invalidMaterials.map((m) => m.type))].join(', ');
                    validation.errorMessages.push(
                        `MTV Purchase Requests can only contain materials with types ZSIM, ZTRV, or ZTRN. Found invalid material type(s): ${invalidTypes}. Please remove or modify these materials before submitting.`,
                    );
                }
            }
        }

        // If way of buying is Framework Requisition, check the validity start and end dates
        if (getValueFromLabelValue(wayOfBuying) === 'FO') {
            this.isValueMissing(isValidDate(validityStart), 'Validity Start', 'General details', validation);
            this.isValueMissing(isValidDate(validityEnd), 'Validity End', 'General Details', validation);
            this.isNotPastDate(validityEnd, wayOfBuying.value, validation, 'General Details');
        } else { // Otherwise, we check the requestedDeliveryDate
            this.isValueMissing(isValidDate(requestedDeliveryDate), 'Requested delivery date', 'General details', validation);
            this.isNotPastDate(requestedDeliveryDate, wayOfBuying.value, validation, 'General Details');
        }

        if (hasException) {
            this.isValueMissing(selectedException, '', '', validation, 'You have to choose an Exception Reason.');
        }
        this.materialDateIsNotPastDate(materials, validation);
        this.materialsCurrencyCheck(materials, validation);

        // Here we check if there is a falsy value in the quantities and price. And if there is an empty object in currencies
        materials.map((m) => m.name).some((q) => !q) ? validation.errorMessages.push('You have at least one material without code.') : null;
        materials.map((m) => m.description).some((q) => !q) ? validation.errorMessages.push('You have at least one material without description.') : null;
        materials.map((m) => m.quantity).some((q) => !q) ? validation.errorMessages.push('You have at least one material without quantity.') : null;
        materials.map((m) => m.price).some((p) => !p) ? validation.errorMessages.push('You have at least one material without price.') : null;
        materials.map((m) => m.currency).some((c) => isLabelValueEmpty(c)) ? validation.errorMessages.push('You have at least one material without currency.') : null;
        materials.map((m) => m.unit).some((u) => !u) ? validation.errorMessages.push('You have at least one material without unit.') : null;
        materials.map((m) => m.per).some((u) => !u) ? validation.errorMessages.push('You have at least one material without per value.') : null;
        materials.map((m) => m.requestedDeliveryDate).some((u) => !u) ? validation.errorMessages.push('You have at least one material without requested delivery date.') : null;
        materials.map((m) => m.deliveryAddress).some((u) => !u) ? validation.errorMessages.push('You have at least one material without delivery address.') : null;
        materials.map((m) => m.plant).some((u) => !u) ? validation.errorMessages.push('You have at least one material without plant.') : null;

        if (areAccountSplitByMaterial) {
            this.isSomethingMissingInAccounts(accountDetailsByMaterial, validation);
        } else {
            // Here we create a fake AccountDetailsByMaterial[] to use the same function
            this.isSomethingMissingInAccounts([new AccountDetailsByMaterial(null, accounts)], validation);
        }

        const attachments = [...mainAttachments, ...cbAttachments];
        const isThereAnyAttachmentWithoutTopic = attachments.find((a) => !a.topic);
        if (isThereAnyAttachmentWithoutTopic) {
            validation.errorMessages.push('You have at least one attachment under CB justification with no topic selected.');
        }
        this.isLableValueSame(newPaymentTerms, 'Payment Terms', 'General Details', validation);

        validation.error = validation.errorMessages.length > 0;
    }

    /**
     * @param {BrickContext} $
     * @param {{}} requester
     * @param {{}} spender
     * @param {string} prTag
     * @param {{}} denialInfo
     * @param {string} readableDate
     * @param {string} vendorCode
     * @param {string} vendorName
     * @param {string} l1Category
     * @param {string} materialName
     * @param {any} sendEmail
     * @param {string} EPRid
     * @return {Promise<string>}
     */
    static denyRequest(
        $,
        requester,
        spender,
        prTag,
        denialInfo,
        readableDate,
        vendorCode,
        vendorName,
        l1Category,
        materialName,
        sendEmail,
        EPRid,
    ) {
        return this.sendRejectEmailNotification(
            $,
            requester,
            spender,
            prTag,
            denialInfo.denialReason,
            readableDate,
            vendorCode,
            vendorName,
            l1Category,
            materialName,
            sendEmail,
            EPRid,
            requester,
            spender,
            'Denied',
        );
    }

    /**
     * @param {BrickContext} $
     * @param {{}} requester
     * @param {{}} spender
     * @param {string} prTag
     * @param {string} rejectionMsg
     * @param {string} readableDate
     * @param {string} vendorCode
     * @param {string} vendorName
     * @param {string} l1Category
     * @param {string} materialName
     * @param {any} sendEmail
     * @param {string} EPRid
     * @return {Promise<string>}
     */
    static rejectRequest(
        $,
        requester,
        spender,
        prTag,
        rejectionMsg,
        readableDate,
        vendorCode,
        vendorName,
        l1Category,
        materialName,
        sendEmail,
        EPRid,
    ) {
        return this.sendRejectEmailNotification(
            $,
            requester,
            spender,
            prTag,
            rejectionMsg,
            readableDate,
            vendorCode,
            vendorName,
            l1Category,
            materialName,
            sendEmail,
            EPRid,
            spender,
            requester,
            PurchaseRequest.statuses.incompleteSpender,
        );
    }

    /**
     * @param {BrickContext} $
     * @param {{}} requester
     * @param {{}} spender
     * @param {string} prTag
     * @param {string} message
     * @param {string} readableDate
     * @param {string} vendorCode
     * @param {string} vendorName
     * @param {string} l1Category
     * @param {string} materialName
     * @param {any} sendEmail
     * @param {string} EPRid
     * @param {{}} toUser
     * @param {{}} ccUser
     * @param {string} newStatus
     * @return {Promise<string>}
     */
    static sendRejectEmailNotification(
        $,
        requester,
        spender,
        prTag,
        message,
        readableDate,
        vendorCode,
        vendorName,
        l1Category,
        materialName,
        sendEmail,
        EPRid,
        toUser,
        ccUser,
        newStatus,
    ) {
        let mailAddress = toUser.email;
        const ccMailAddress = ccUser.email;
        const env = Config.getParameter('ENV');

        if (env === 'codev') {
            mailAddress += ';jael.dubey@olympe.ch';
        }

        const mailContent = getMailContentPRRejected(
            toUser,
            requester,
            spender,
            readableDate,
            vendorCode,
            vendorName,
            l1Category,
            materialName,
            message,
            prTag,
            EPRid,
            newStatus,
        );

        return sendEmailLambda($, sendEmail, mailAddress, ccMailAddress, '', `[Easy Request] ${env !== 'prod' ? (env === 'codev' ? '[DEV] ' : '[PRE-PROD] ') : ''}PR denied`, mailContent, []);
    }
}

/**
 * @typedef {{errorMessages: string[], error: boolean}} PRValidation
 */

/**
 * @typedef {{reasons: string[], approvalNeeded: boolean}} ApprovalNeeded
 */
